import React, { useState, useEffect, useLayoutEffect, useMemo, useCallback, useRef } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Helmet } from 'react-helmet-async';
import { Search, Store, MapPin, Package, ChevronLeft, Loader2 } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { ProductGrid } from '@/components/products';
import { stores as storesApi } from '@/lib/api';
import { buildPathForLocale } from '@/lib/intl-seo';
import { getPublicSiteUrl } from '@/lib/seo';
import { PageBreadcrumb } from '@/components/seo/PageBreadcrumb';

const PAGE_SIZE = 24;

function normalizeImageUrl(url) {
  if (!url) return null;
  if (url.startsWith('/uploads/')) return url.replace('/uploads/', '/api/uploads/');
  return url;
}

const SellerStorePage = () => {
  const { lang, storeSlug } = useParams();
  const { t, i18n } = useTranslation();
  const langCode = (lang || i18n.language || 'tr').substring(0, 2);
  const site = getPublicSiteUrl();

  const [store, setStore] = useState(null);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [productsLoading, setProductsLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  const [searchInput, setSearchInput] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('');
  const [totalProducts, setTotalProducts] = useState(0);

  const nextPageRef = useRef(2);
  const loadMoreLockRef = useRef(false);
  const loadMoreSentinelRef = useRef(null);
  const fetchGenerationRef = useRef(0);

  const homePath = buildPathForLocale(langCode, []);
  const productsPath = buildPathForLocale(langCode, ['products']);

  const listResetKey = useMemo(
    () => `${storeSlug}|${searchQuery.trim()}|${selectedCategory}|${langCode}`,
    [storeSlug, searchQuery, selectedCategory, langCode],
  );

  const fetchStore = useCallback(async () => {
    try {
      setLoading(true);
      const res = await storesApi.get(storeSlug);
      setStore(res.data);
    } catch {
      setStore(null);
    } finally {
      setLoading(false);
    }
  }, [storeSlug]);

  useEffect(() => {
    fetchStore();
  }, [fetchStore]);

  useEffect(() => {
    const timer = setTimeout(() => setSearchQuery(searchInput), 350);
    return () => clearTimeout(timer);
  }, [searchInput]);

  useLayoutEffect(() => {
    setProducts([]);
    setTotalProducts(0);
    setHasMore(false);
    setProductsLoading(true);
    setLoadingMore(false);
    nextPageRef.current = 2;
    loadMoreLockRef.current = false;
  }, [listResetKey]);

  useEffect(() => {
    if (!storeSlug) return;

    const gen = ++fetchGenerationRef.current;
    const ac = new AbortController();

    const loadFirstPage = async () => {
      try {
        const res = await storesApi.products(storeSlug, {
          page: 1,
          limit: PAGE_SIZE,
          lang: langCode,
          search: searchQuery.trim() || undefined,
          category: selectedCategory || undefined,
        });

        if (gen !== fetchGenerationRef.current || ac.signal.aborted) return;

        const batch = res.data?.products || [];
        setProducts(batch);
        setTotalProducts(res.data?.total || 0);
        const totalPages = res.data?.total_pages || 1;
        setHasMore(1 < totalPages || batch.length >= PAGE_SIZE);
        nextPageRef.current = 2;
      } catch {
        if (gen === fetchGenerationRef.current && !ac.signal.aborted) {
          setProducts([]);
          setHasMore(false);
        }
      } finally {
        if (gen === fetchGenerationRef.current && !ac.signal.aborted) {
          setProductsLoading(false);
        }
      }
    };

    loadFirstPage();
    return () => ac.abort();
  }, [listResetKey, storeSlug, langCode, searchQuery, selectedCategory]);

  const loadMore = useCallback(async () => {
    if (!storeSlug || !hasMore || productsLoading || loadingMore || loadMoreLockRef.current) return;
    loadMoreLockRef.current = true;
    setLoadingMore(true);
    try {
      const pageNum = nextPageRef.current;
      const res = await storesApi.products(storeSlug, {
        page: pageNum,
        limit: PAGE_SIZE,
        lang: langCode,
        search: searchQuery.trim() || undefined,
        category: selectedCategory || undefined,
      });
      const batch = res.data?.products || [];
      const totalPages = res.data?.total_pages || pageNum;
      setProducts((prev) => {
        const seen = new Set(prev.map((p) => p.id));
        const merged = [...prev];
        for (const item of batch) {
          if (!seen.has(item.id)) {
            seen.add(item.id);
            merged.push(item);
          }
        }
        return merged;
      });
      setTotalProducts(res.data?.total ?? 0);
      setHasMore(pageNum < totalPages || batch.length >= PAGE_SIZE);
      nextPageRef.current = pageNum + 1;
    } catch (e) {
      console.error('Store load more failed:', e);
    } finally {
      loadMoreLockRef.current = false;
      setLoadingMore(false);
    }
  }, [storeSlug, hasMore, productsLoading, loadingMore, langCode, searchQuery, selectedCategory]);

  useEffect(() => {
    if (!hasMore || productsLoading) return;
    const el = loadMoreSentinelRef.current;
    if (!el) return;
    const ob = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting) loadMore();
      },
      { root: null, rootMargin: '240px', threshold: 0 },
    );
    ob.observe(el);
    return () => ob.disconnect();
  }, [hasMore, productsLoading, loadMore, products.length]);

  const pageTitle = useMemo(() => {
    if (!store?.name) return t('store.pageTitle', 'Satıcı Mağazası');
    return `${store.name} | ${t('store.sellerStore', 'Satıcı Mağazası')}`;
  }, [store, t]);

  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <div className="container mx-auto px-4 py-8">
          <Skeleton className="h-40 w-full rounded-2xl mb-8" />
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {[...Array(8)].map((_, i) => (
              <Skeleton key={i} className="h-56 rounded-xl" />
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (!store) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center px-4">
        <div className="text-center max-w-md">
          <Store className="w-16 h-16 mx-auto text-muted-foreground mb-4" />
          <h1 className="text-xl font-semibold mb-2">{t('store.notFound', 'Mağaza bulunamadı')}</h1>
          <p className="text-muted-foreground text-sm mb-6">
            {t('store.notFoundHint', 'Aradığınız satıcı mağazası mevcut değil veya henüz yayında değil.')}
          </p>
          <Button asChild variant="outline">
            <Link to={productsPath}>
              <ChevronLeft className="w-4 h-4 mr-2" />
              {t('store.backToProducts', 'Ürünlere dön')}
            </Link>
          </Button>
        </div>
      </div>
    );
  }

  const logoUrl = normalizeImageUrl(store.logo);

  return (
    <div className="min-h-screen bg-background" data-testid="seller-store-page">
      <Helmet>
        <title>{pageTitle}</title>
        {store.description && <meta name="description" content={store.description.substring(0, 160)} />}
        {site && <link rel="canonical" href={`${site.replace(/\/$/, '')}${buildPathForLocale(langCode, ['store', store.slug || storeSlug])}`} />}
      </Helmet>

      <PageBreadcrumb
        items={[
          { label: t('nav.home', 'Ana Sayfa'), href: homePath },
          { label: t('store.sellers', 'Satıcılar'), href: productsPath },
          { label: store.name },
        ]}
      />

      {/* Store hero */}
      <section className="bg-gradient-to-br from-primary/90 via-primary to-primary/80 text-primary-foreground">
        <div className="container mx-auto px-4 py-8 md:py-10">
          <div className="flex flex-col sm:flex-row items-start sm:items-center gap-5">
            <div className="w-20 h-20 md:w-24 md:h-24 rounded-2xl bg-white/15 backdrop-blur border border-white/20 flex items-center justify-center overflow-hidden shrink-0">
              {logoUrl ? (
                <img src={logoUrl} alt={store.name} className="w-full h-full object-cover" />
              ) : (
                <Store className="w-10 h-10 md:w-12 md:h-12" />
              )}
            </div>
            <div className="flex-1 min-w-0">
              <h1 className="text-2xl md:text-3xl font-bold tracking-tight">{store.name}</h1>
              {store.description && (
                <p className="text-primary-foreground/85 mt-2 text-sm md:text-base max-w-2xl line-clamp-3">
                  {store.description}
                </p>
              )}
              <div className="flex flex-wrap items-center gap-3 mt-3 text-sm text-primary-foreground/90">
                <span className="inline-flex items-center gap-1">
                  <Package className="w-4 h-4" />
                  {totalProducts || store.product_count} {t('store.products', 'ürün')}
                </span>
                {(store.city || store.country) && (
                  <span className="inline-flex items-center gap-1">
                    <MapPin className="w-4 h-4" />
                    {[store.city, store.country].filter(Boolean).join(', ')}
                  </span>
                )}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Search + categories */}
      <section className="sticky top-[64px] z-20 bg-background border-b shadow-sm">
        <div className="container mx-auto px-4 py-4 space-y-3">
          <div className="relative max-w-xl">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              type="search"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              placeholder={t('store.searchInStore', 'Bu mağazada ara...')}
              className="pl-9 h-10 rounded-full"
              data-testid="store-search-input"
            />
          </div>
          {store.categories?.length > 0 && (
            <div className="flex gap-2 overflow-x-auto pb-1 scrollbar-thin">
              <Button
                type="button"
                size="sm"
                variant={selectedCategory ? 'outline' : 'default'}
                className="rounded-full shrink-0"
                onClick={() => setSelectedCategory('')}
              >
                {t('store.allCategories', 'Tümü')}
              </Button>
              {store.categories.map((cat) => (
                <Button
                  key={cat.slug || cat.name}
                  type="button"
                  size="sm"
                  variant={selectedCategory === cat.name ? 'default' : 'outline'}
                  className="rounded-full shrink-0"
                  onClick={() => setSelectedCategory(cat.name)}
                >
                  {cat.name}
                  <Badge variant="secondary" className="ml-1.5 text-[10px] px-1.5">
                    {cat.count}
                  </Badge>
                </Button>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Products — infinite scroll */}
      <section className="container mx-auto px-4 py-8">
        <ProductGrid
          products={products}
          loading={productsLoading}
          columns={4}
          listContextKey={`store-${store.slug}-${listResetKey}`}
        />
        {!productsLoading && products.length > 0 && (
          <>
            <div ref={loadMoreSentinelRef} className="h-4 w-full" aria-hidden data-testid="store-infinite-sentinel" />
            {loadingMore && (
              <div className="flex justify-center py-8" data-testid="store-loading-more">
                <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
              </div>
            )}
            {!hasMore && !loadingMore && products.length > 0 && (
              <p className="text-center text-sm text-muted-foreground py-6">
                {t('store.allLoaded', 'Tüm ürünler yüklendi')} ({products.length})
              </p>
            )}
          </>
        )}
      </section>
    </div>
  );
};

export default SellerStorePage;
